import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Package,
  ShoppingCart,
  CheckCircle,
  XCircle,
  Clock,
  Truck,
  Bell,
  Wrench,
  Search
} from "lucide-react";
import { AIPartsEstimateButton } from "@/components/AITools";

const STATUS_CONFIG = {
  de_comandat: { label: "De comandat", labelAr: "يجب الطلب", color: "bg-orange-500 text-white", icon: ShoppingCart },
  comandat: { label: "Comandat", labelAr: "تم الطلب", color: "bg-blue-500 text-white", icon: Truck },
  receptionat: { label: "Recepționat", labelAr: "تم الاستلام", color: "bg-green-500 text-white", icon: CheckCircle },
  indisponibil: { label: "Indisponibil", labelAr: "غير متوفر", color: "bg-red-500 text-white", icon: XCircle },
};

export default function MechanicPartsPage() {
  const [orders, setOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchOrders();
    fetchNotifications();
  }, [filter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/mechanic-parts-orders?status=${filter}`);
      setOrders(resp.data);
    } catch (err) {
      toast.error("Eroare la incarcarea comenzilor");
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const resp = await api.get("/mechanic-notifications");
      setNotifications(resp.data.filter(n => !n.read));
    } catch (err) {
      console.error("Notifications fetch error:", err);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/mechanic-parts-orders/${orderId}/status?status=${newStatus}`);
      toast.success(`Status actualizat: ${STATUS_CONFIG[newStatus]?.label}`);
      fetchOrders();
      if (newStatus === "receptionat") {
        toast.info("Mecanicul a fost notificat ca piesa a sosit");
      }
    } catch (err) {
      toast.error("Eroare la actualizarea statusului");
    }
  };

  const deleteOrder = async (orderId) => {
    try {
      await api.delete(`/mechanic-parts-orders/${orderId}`);
      toast.success("Comanda stearsa");
      fetchOrders();
    } catch (err) {
      toast.error("Eroare");
    }
  };

  const markNotifRead = async (notifId) => {
    try {
      await api.put(`/mechanic-notifications/${notifId}/read`);
      setNotifications(prev => prev.filter(n => n.notification_id !== notifId));
    } catch (err) {
      console.error("Mark notification read error:", err);
    }
  };

  const filteredOrders = searchQuery
    ? orders.filter(o =>
        (o.item_label || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (o.scooter_id || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (o.scooter_brand || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (o.scooter_model || "").toLowerCase().includes(searchQuery.toLowerCase())
      )
    : orders;

  const stats = {
    de_comandat: orders.filter(o => o.status === "de_comandat").length,
    comandat: orders.filter(o => o.status === "comandat").length,
    receptionat: orders.filter(o => o.status === "receptionat").length,
    indisponibil: orders.filter(o => o.status === "indisponibil").length,
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-4 space-y-6" data-testid="mechanic-parts-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Package className="w-6 h-6 text-primary" />
              Piese de Comandat / قطع للطلب
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Gestionare comenzi piese mecanice
            </p>
          </div>
        </div>

        {/* Notifications */}
        {notifications.length > 0 && (
          <div className="space-y-2">
            {notifications.map(n => (
              <div key={n.notification_id} className="flex items-start gap-3 bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <Bell className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm">{n.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(n.created_at).toLocaleString("ro-RO")}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => markNotifRead(n.notification_id)}>
                  Citit
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (
              <Card key={key} className="border-border cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => setFilter(key)}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${cfg.color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xl font-bold font-mono">{stats[key]}</p>
                    <p className="text-xs text-muted-foreground">{cfg.label}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-secondary/50 rounded-lg px-3 py-1.5 flex-1 max-w-md">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cauta piesa, scuter..."
              className="bg-transparent text-sm outline-none flex-1"
              data-testid="parts-search"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="parts-filter">
              <SelectValue placeholder="Filtru status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate</SelectItem>
              <SelectItem value="de_comandat">De comandat</SelectItem>
              <SelectItem value="comandat">Comandat</SelectItem>
              <SelectItem value="receptionat">Receptionat</SelectItem>
              <SelectItem value="indisponibil">Indisponibil</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="text-center py-10 text-muted-foreground">Se incarca...</div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Nicio comanda de piese</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map(order => {
              const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.de_comandat;
              const Icon = cfg.icon;
              return (
                <Card key={order.order_id} className="border-border" data-testid={`order-${order.order_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      {/* Left: Part Info */}
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 mb-1">
                          <Wrench className="w-4 h-4 text-primary" />
                          <span className="font-bold text-base">{order.item_label}</span>
                        </div>
                        <div className="text-sm text-muted-foreground space-y-0.5">
                          <p>
                            <span className="font-medium text-foreground">{order.scooter_brand} {order.scooter_model}</span>
                            {" "}&bull;{" "}
                            <span className="font-mono">{order.scooter_id}</span>
                          </p>
                          {order.notes && <p className="italic">Note: {order.notes}</p>}
                          <p className="text-xs">
                            Creat de: <span className="font-medium">{order.created_by}</span> &bull; {new Date(order.created_at).toLocaleDateString("ro-RO")}
                          </p>
                        </div>
                      </div>

                      {/* Right: Status + Actions */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={cfg.color}>
                          <Icon className="w-3 h-3 mr-1" />
                          {cfg.label}
                        </Badge>

                        {order.status === "de_comandat" && (
                          <Button size="sm" variant="outline" onClick={() => updateStatus(order.order_id, "comandat")}
                            data-testid={`mark-comandat-${order.order_id}`}>
                            <Truck className="w-3 h-3 mr-1" /> Comandat
                          </Button>
                        )}
                        {order.status === "comandat" && (
                          <>
                            <Button size="sm" variant="outline" className="border-green-500 text-green-500 hover:bg-green-500/10"
                              onClick={() => updateStatus(order.order_id, "receptionat")}
                              data-testid={`mark-receptionat-${order.order_id}`}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Receptionat
                            </Button>
                            <Button size="sm" variant="outline" className="border-red-500 text-red-500 hover:bg-red-500/10"
                              onClick={() => updateStatus(order.order_id, "indisponibil")}>
                              <XCircle className="w-3 h-3 mr-1" /> Indisponibil
                            </Button>
                          </>
                        )}
                        {(order.status === "receptionat" || order.status === "indisponibil") && (
                          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => deleteOrder(order.order_id)}>
                            Sterge
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* AI Price Estimation */}
                    {(order.status === "de_comandat" || order.status === "comandat") && (
                      <div className="mt-3 pt-3 border-t border-border/50">
                        <AIPartsEstimateButton
                          itemLabel={order.item_label}
                          scooterBrand={order.scooter_brand}
                          scooterModel={order.scooter_model}
                          scooterYear={0}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
